import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { ArrowRight, CheckCircle, Search, MapPin, Building2, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate('/properties');
  };

  return (
    <section className="relative flex items-center justify-center overflow-hidden bg-white dark:bg-gray-900">
      {/* Simple white background with subtle gradient */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" />

      {/* Fluid Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto hero-fluid-container">
        <div className="w-full">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-[clamp(1rem,2vw,2rem)] animate-fade-in">
            <div className="bg-[#12532c] text-white rounded-full font-semibold shadow-lg hero-fluid-badge" data-testid="hero-badge-dld">
              {t({ en: 'DLD & REIDIN Verified Data', ar: 'بيانات معتمدة من دائرة الأراضي' })}
            </div>
            <div className="bg-[#C5A038] text-white rounded-full font-semibold shadow-lg hero-fluid-badge" data-testid="hero-badge-intelligence">
              {t({ en: 'Institutional-Grade Intelligence', ar: 'تحليلات مؤسسية احترافية' })}
            </div>
          </div>

          {/* Fluid Main Heading */}
          <h1 className="hero-fluid-title font-bold text-[#12532c] dark:text-emerald-400 animate-slide-up" data-testid="hero-title">
            {t({
              en: 'Where Capital Meets Clarity',
              ar: 'حيث يلتقي رأس المال بالوضوح'
            })}
          </h1>

          {/* Fluid Description */}
          <p className="hero-fluid-description text-gray-700 dark:text-gray-300 animate-slide-up font-normal" style={{ animationDelay: '0.1s' }} data-testid="hero-description">
            {t({
              en: "Access institutional-grade Dubai real estate intelligence. Verified data, honest risk assessment, and investment logic sourced from DLD, REIDIN, and Property Monitor - curated for high-net-worth investors who expect precision not persuasion.",
              ar: 'احصل على معلومات عقارية احترافية لدبي. بيانات موثقة، تقييم صادق للمخاطر، ومنطق استثماري من دائرة الأراضي، REIDIN، و Property Monitor - مُصمم للمستثمرين ذوي الثروات العالية الذين يتوقعون الدقة وليس الإقناع.'
            })}
          </p>

          {/* Feature List */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-[clamp(1rem,2vw,2rem)] animate-slide-up" style={{ animationDelay: '0.15s' }}>
            {[
              { en: 'Verified Data', ar: 'بيانات موثقة' },
              { en: 'Strategic Analysis', ar: 'تحليل استراتيجي' },
              { en: 'Honest Assessment', ar: 'تقييم صادق' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hero-fluid-chip">
                <CheckCircle className="w-4 h-4 text-[#12532c] dark:text-emerald-400 flex-shrink-0" />
                <span className="font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{t(item)}</span>
              </div>
            ))}
          </div>

          {/* Search Bar */}
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-[clamp(0.5rem,0.8vw,1rem)] mb-[clamp(1rem,2vw,2rem)] animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2 md:gap-3" data-testid="hero-search-form">
              <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg min-w-0 hero-fluid-input">
                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t({ en: 'Location (e.g., Business Bay)', ar: 'الموقع (الخليج التجاري)' })}
                  className="flex-1 min-w-0 bg-transparent border-none outline-none text-gray-800 placeholder-gray-400"
                  data-testid="hero-search-location"
                />
              </div>
              <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg min-w-0 hero-fluid-input">
                <Building2 className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                <select className="flex-1 min-w-0 bg-transparent border-none outline-none text-gray-800" data-testid="hero-search-type">
                  <option>{t({ en: 'Property Type', ar: 'نوع العقار' })}</option>
                  <option>{t({ en: 'Warehouse', ar: 'مستودع' })}</option>
                  <option>{t({ en: 'Staff Accommodation', ar: 'سكن الموظفين' })}</option>
                  <option>{t({ en: 'Retail / Shop', ar: 'تجزئة / محل' })}</option>
                  <option>{t({ en: 'Office', ar: 'مكتب' })}</option>
                </select>
              </div>
              <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg min-w-0 hero-fluid-input">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                <select className="flex-1 min-w-0 bg-transparent border-none outline-none text-gray-800" data-testid="hero-search-budget">
                  <option>{t({ en: 'Budget', ar: 'الميزانية' })}</option>
                  <option>{t({ en: 'Under AED 5M', ar: 'أقل من 5 مليون' })}</option>
                  <option>{t({ en: 'AED 5M - 10M', ar: '5-10 مليون' })}</option>
                  <option>{t({ en: 'Above AED 10M', ar: 'أكثر من 10 مليون' })}</option>
                </select>
              </div>
              <Button
                type="submit"
                className="bg-[#12532c] hover:bg-[#12532c]/90 text-white rounded-lg font-semibold flex items-center justify-center gap-2 flex-shrink-0 hero-fluid-btn"
                data-testid="hero-search-submit"
              >
                <Search className="w-4 h-4 md:w-5 md:h-5" />
                {t({ en: 'Search', ar: 'بحث' })}
              </Button>
            </form>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-[#C5A038] hover:bg-[#b08f2f] text-white rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold border-2 border-[#C5A038] hero-fluid-cta"
              data-testid="hero-cta-get-started"
            >
              {t({ en: 'Get Started', ar: 'ابدأ الآن' })}
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/properties')}
              className="bg-white text-[#12532c] hover:bg-gray-50 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold border-2 border-[#12532c] hero-fluid-cta"
              data-testid="hero-cta-explore"
            >
              {t({ en: 'Explore Properties', ar: 'استكشف العقارات' })}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
